import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Contract, ContractType, PaymentFrequency, ReminderPeriod, ReminderFrequency, Attachment, ContactPerson, ContactRole, Category, PaymentFrequencyUnit, ContractDirection, PaymentType } from "@/lib/types";

export interface ContractFormData {
  supplierName: string;
  organisationnumber?: string | null;
  importance: 1 | 2 | 3 | 4 | 5;
  type: ContractType;
  expectedCost: number;
  currencyId?: string;
  paymentFrequency: PaymentFrequency;
  signedDate: Date;
  expiryDate: Date;
  reminderPeriod: ReminderPeriod;
  reminderFrequency: ReminderFrequency;
  categoryId: string | null;
  contactPersonIds: string[];
  attachments: File[];
  use_services_cost?: boolean;
  paymentType?: PaymentType;
}

export function useContracts() {
  const queryClient = useQueryClient();

  const ensureValidPaymentFrequencyUnit = (value: string | undefined): PaymentFrequencyUnit => {
    if (value === "Day" || value === "Month" || value === "Year") {
      return value;
    }
    return "Month"; // Default fallback
  };

  const fetchContracts = async (): Promise<Contract[]> => {
    const { data, error } = await supabase
      .from("contracts")
      .select(`
        *,
        category:categories(*),
        currency:currencies(*),
        group:contract_groups(*)
      `)
      .order("supplier_name");

    if (error) {
      toast.error(`Error fetching contracts: ${error.message}`);
      throw error;
    }

    if (!data) {
      return [];
    }

    const contractsWithRelations = await Promise.all(
      data.map(async (contract) => {
        if (!contract || !contract.id) {
          throw new Error("Invalid contract data received");
        }
        
        let contractType: ContractType;
        
        if (contract.contract_type_id) {
          const { data: contractTypeData, error: contractTypeError } = await supabase
            .from("contract_types")
            .select("*")
            .eq("id", contract.contract_type_id)
            .single();
            
          if (contractTypeError || !contractTypeData) {
            contractType = {
              id: "",
              name: contract.type,
              description: "",
              auto_renews: false,
              renewal_interval: undefined,
              renewal_unit: undefined,
              direction: undefined,
              created_at: undefined,
              updated_at: undefined,
            };
          } else {
            contractType = {
              id: contractTypeData.id,
              name: contractTypeData.name,
              description: contractTypeData.description || undefined,
              created_at: contractTypeData.created_at,
              updated_at: contractTypeData.updated_at,
              auto_renews: contractTypeData.auto_renews || false,
              renewal_interval: contractTypeData.renewal_interval,
              renewal_unit: contractTypeData.renewal_unit ? ensureValidPaymentFrequencyUnit(contractTypeData.renewal_unit) : undefined,
              direction: contractTypeData.direction as ContractDirection
            };
          }
        } else {
          const { data: contractTypeData, error: contractTypeError } = await supabase
            .from("contract_types")
            .select("*")
            .eq("name", contract.type)
            .single();
            
          if (contractTypeError || !contractTypeData) {
            contractType = {
              id: "",
              name: contract.type,
              description: "",
              auto_renews: false,
              renewal_interval: undefined,
              renewal_unit: undefined,
              direction: undefined,
              created_at: undefined,
              updated_at: undefined,
            };
          } else {
            contractType = {
              id: contractTypeData.id,
              name: contractTypeData.name,
              description: contractTypeData.description || undefined,
              created_at: contractTypeData.created_at,
              updated_at: contractTypeData.updated_at,
              auto_renews: contractTypeData.auto_renews || false,
              renewal_interval: contractTypeData.renewal_interval,
              renewal_unit: contractTypeData.renewal_unit ? ensureValidPaymentFrequencyUnit(contractTypeData.renewal_unit) : undefined,
              direction: contractTypeData.direction as ContractDirection
            };
          }
        }
        
        let paymentFrequency: PaymentFrequency;
        
        try {
          if (typeof contract.payment_frequency === 'string') {
            try {
              const parsed = JSON.parse(contract.payment_frequency);
              if (parsed && typeof parsed === 'object' && 'interval' in parsed && 'unit' in parsed) {
                paymentFrequency = {
                  interval: parsed.interval,
                  unit: parsed.unit
                };
              } else {
                paymentFrequency = parseLegacyPaymentFrequency(contract.payment_frequency);
              }
            } catch (e) {
              paymentFrequency = parseLegacyPaymentFrequency(contract.payment_frequency);
            }
          } else if (contract.payment_frequency && typeof contract.payment_frequency === 'object') {
            paymentFrequency = contract.payment_frequency as PaymentFrequency;
          } else {
            paymentFrequency = {
              interval: 1,
              unit: "Month"
            };
          }
        } catch (e) {
          console.error("Error parsing payment frequency:", e);
          paymentFrequency = {
            interval: 1,
            unit: "Month"
          };
        }
        
        const { data: contactsData, error: contactsError } = await supabase
          .from("contract_contacts")
          .select(`
            contact_id
          `)
          .eq("contract_id", contract.id);

        let contactPersons: ContactPerson[] = [];
        if (!contactsError && contactsData && contactsData.length > 0) {
          const contactIds = contactsData
            .filter(item => item && item.contact_id)
            .map((item) => item.contact_id);
          
          if (contactIds.length > 0) {
            const { data: contactPersonsData, error: contactPersonsError } = await supabase
              .from("contact_persons")
              .select("*")
              .in("id", contactIds);

            if (!contactPersonsError && contactPersonsData && contactPersonsData.length > 0) {
              contactPersons = contactPersonsData
                .filter(person => person) // Filter out null/undefined values
                .map(person => ({
                  id: person.id,
                  name: person.name,
                  email: person.email,
                  phone: person.phone || undefined,
                  role: (person.role as ContactRole) || "Other" as ContactRole,
                  created_at: person.created_at,
                  updated_at: person.updated_at,
                }));
            }
          }
        }

        const { data: attachmentsData, error: attachmentsError } = await supabase
          .from("attachments")
          .select("*")
          .eq("contract_id", contract.id);

        let attachments: Attachment[] = [];
        if (!attachmentsError && attachmentsData && attachmentsData.length > 0) {
          attachments = await Promise.all(
            attachmentsData
              .filter(att => att) // Filter out null/undefined values
              .map(async (att) => {
                const signedUrl = await getSignedUrl(att.url);
                
                return {
                  id: att.id,
                  name: att.name,
                  type: att.type,
                  size: att.size,
                  url: signedUrl,
                  uploaded_at: att.uploaded_at,
                  uploadedAt: new Date(att.uploaded_at)
                };
              })
          );
        }

        let category: Category | undefined = undefined;
        if (contract.category) {
          category = {
            id: contract.category.id,
            name: contract.category.name,
            description: contract.category.description || undefined,
            created_at: contract.category.created_at,
            updated_at: contract.category.updated_at,
          };
        }

        let currency = undefined;
        if (contract.currency) {
          currency = {
            id: contract.currency.id,
            code: contract.currency.code,
            name: contract.currency.name,
            symbol: contract.currency.symbol,
            created_at: contract.currency.created_at,
            updated_at: contract.currency.updated_at,
            user_id: contract.currency.user_id,
          };
        }

        let group = undefined;
        if (contract.group) {
          group = {
            id: contract.group.id,
            name: contract.group.name,
            description: contract.group.description || undefined,
            user_id: contract.group.user_id,
            created_at: contract.group.created_at,
            updated_at: contract.group.updated_at,
            createdAt: new Date(contract.group.created_at),
            updatedAt: new Date(contract.group.updated_at)
          };
        }

        let contractServices = [];
        try {
          const { data: contractServicesData, error: contractServicesError } = await supabase
            .from("contract_services")
            .select(`
              *,
              service:services(*)
            `)
            .eq("contract_id", contract.id);
            
          if (!contractServicesError && contractServicesData && contractServicesData.length > 0) {
            contractServices = contractServicesData.map(service => ({
              id: service.id,
              contract_id: service.contract_id,
              service_id: service.service_id,
              service: service.service ? {
                id: service.service.id,
                name: service.service.name,
                description: service.service.description,
                service_type: service.service.service_type,
                cost: service.service.cost,
                currency_id: service.service.currency_id,
                currency: null, // Will be filled below if needed
                user_id: service.service.user_id,
                created_at: service.service.created_at,
                updated_at: service.service.updated_at
              } : undefined,
              quantity: service.quantity,
              user_id: service.user_id,
              created_at: service.created_at,
              updated_at: service.updated_at
            }));
            
            // Fetch currencies for services if needed
            for (const contractService of contractServices) {
              if (contractService.service && contractService.service.currency_id) {
                const { data: currencyData } = await supabase
                  .from("currencies")
                  .select("*")
                  .eq("id", contractService.service.currency_id)
                  .single();
                  
                if (currencyData) {
                  contractService.service.currency = {
                    id: currencyData.id,
                    code: currencyData.code,
                    name: currencyData.name,
                    symbol: currencyData.symbol,
                    created_at: currencyData.created_at,
                    updated_at: currencyData.updated_at,
                    user_id: currencyData.user_id
                  };
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching contract services:", error);
        }

        return {
          id: contract.id,
          supplierName: contract.supplier_name,
          organisationnumber: contract.organisationnumber || undefined,
          importance: contract.importance as 1 | 2 | 3 | 4 | 5,
          type: contractType,
          expectedCost: contract.expected_cost,
          paymentFrequency: paymentFrequency,
          signedDate: new Date(contract.signed_date),
          expiryDate: new Date(contract.expiry_date),
          reminderPeriod: contract.reminder_period as ReminderPeriod,
          reminderFrequency: contract.reminder_frequency as ReminderFrequency,
          categoryId: contract.category_id,
          category,
          contactPersons,
          attachments,
          created_at: contract.created_at,
          updated_at: contract.updated_at,
          currencyId: contract.currency_id,
          currency,
          groupId: contract.group_id,
          group,
          typeId: contract.contract_type_id,
          userId: contract.user_id,
          use_services_cost: contract.use_services_cost || false,
          paymentType: contract.payment_type as PaymentType || 'BankTransfer' as PaymentType,
          contractServices: contractServices
        };
      })
    );

    return contractsWithRelations;
  };

  const parseLegacyPaymentFrequency = (frequencyString: string): PaymentFrequency => {
    switch (frequencyString) {
      case "Monthly":
        return { interval: 1, unit: "Month" };
      case "Quarterly":
        return { interval: 3, unit: "Month" };
      case "Semi-annually":
        return { interval: 6, unit: "Month" };
      case "Annually":
        return { interval: 1, unit: "Year" };
      case "One-time":
        return { interval: 0, unit: "Day" };
      case "Other":
      default:
        return { interval: 1, unit: "Month" };
    }
  };

  const fetchContactPersons = async (contractId: string): Promise<ContactPerson[]> => {
    const { data: contactsData, error: contactsError } = await supabase
      .from("contract_contacts")
      .select(`
        contact_id
      `)
      .eq("contract_id", contractId);

    if (contactsError || !contactsData || contactsData.length === 0) {
      return [];
    }

    const contactIds = contactsData
      .filter(item => item && item.contact_id)
      .map((item) => item.contact_id);
    
    if (contactIds.length === 0) {
      return [];
    }

    const { data: contactPersonsData, error: contactPersonsError } = await supabase
      .from("contact_persons")
      .select("*")
      .in("id", contactIds);

    if (contactPersonsError || !contactPersonsData) {
      return [];
    }

    return contactPersonsData
      .filter(person => person) // Filter out null/undefined values
      .map(person => ({
        id: person.id,
        name: person.name,
        email: person.email,
        phone: person.phone || undefined,
        role: (person.role as ContactRole) || "Other" as ContactRole,
        created_at: person.created_at,
        updated_at: person.updated_at,
      }));
  };

  const fetchAttachments = async (contractId: string): Promise<Attachment[]> => {
    const { data: attachmentsData, error: attachmentsError } = await supabase
      .from("attachments")
      .select("*")
      .eq("contract_id", contractId);

    if (attachmentsError || !attachmentsData || attachmentsData.length === 0) {
      return [];
    }

    return await Promise.all(
      attachmentsData
        .filter(att => att) // Filter out null/undefined values
        .map(async (att) => {
          const signedUrl = await getSignedUrl(att.url);
          
          return {
            id: att.id,
            name: att.name,
            type: att.type,
            size: att.size,
            url: signedUrl,
            uploaded_at: att.uploaded_at,
            uploadedAt: new Date(att.uploaded_at)
          };
        })
    );
  };

  const getSignedUrl = async (path: string) => {
    const { data, error } = await supabase.storage
      .from("contract_attachments")
      .createSignedUrl(path, 60 * 60); // 60 minutes expiry
    
    if (error) {
      console.error("Error creating signed URL:", error);
      const { data: publicUrlData } = supabase.storage
        .from("contract_attachments")
        .getPublicUrl(path);
      
      return publicUrlData.publicUrl;
    }
    
    return data.signedUrl;
  };

  const logContractChange = async (
    contractId: string,
    changeType: 'create' | 'update' | 'delete',
    fieldName?: string,
    oldValue?: any,
    newValue?: any,
    metadata?: any
  ) => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from('contract_changes')
      .insert({
        contract_id: contractId,
        user_id: userId,
        change_type: changeType,
        field_name: fieldName,
        old_value: oldValue,
        new_value: newValue,
        metadata: metadata
      });

    if (error) {
      console.error("Error logging contract change:", error);
      throw error;
    }
  };

  const createContract = async (formData: ContractFormData): Promise<Contract> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }

    const paymentFrequencyString = JSON.stringify(formData.paymentFrequency);

    const categoryId = formData.categoryId || null;
    const organisationnumber = formData.organisationnumber || null;
    
    const normalizedSignedDate = new Date(formData.signedDate);
    normalizedSignedDate.setHours(12, 0, 0, 0);
    
    const normalizedExpiryDate = new Date(formData.expiryDate);
    normalizedExpiryDate.setHours(12, 0, 0, 0);

    const { data: newContract, error: contractError } = await supabase
      .from("contracts")
      .insert({
        supplier_name: formData.supplierName,
        organisationnumber: organisationnumber,
        importance: formData.importance,
        type: formData.type.name,
        contract_type_id: formData.type.id,
        expected_cost: formData.expectedCost,
        currency_id: formData.currencyId,
        payment_frequency: paymentFrequencyString,
        signed_date: normalizedSignedDate.toISOString().split('T')[0],
        expiry_date: normalizedExpiryDate.toISOString().split('T')[0],
        reminder_period: formData.reminderPeriod,
        reminder_frequency: formData.reminderFrequency,
        category_id: categoryId,
        user_id: userId,
        use_services_cost: formData.use_services_cost || false,
        payment_type: formData.paymentType || 'BankTransfer',
      })
      .select()
      .single();

    if (contractError || !newContract) {
      toast.error(`Error creating contract: ${contractError?.message || "Unknown error"}`);
      throw contractError || new Error("Failed to create contract");
    }

    const { error: deleteContactsError } = await supabase
      .from("contract_contacts")
      .delete()
      .eq('contract_id', newContract.id);

    if (deleteContactsError) {
      throw deleteContactsError;
    }

    if (formData.contactPersonIds.length > 0) {
      const contactsToInsert = formData.contactPersonIds.map(contactId => ({
        contract_id: newContract.id,
        contact_id: contactId,
        user_id: userId,
      }));

      const { error: insertContactsError } = await supabase
        .from("contract_contacts")
        .insert(contactsToInsert);

      if (insertContactsError) {
        throw insertContactsError;
      }
    }

    let attachments: Attachment[] = [];
    if (formData.attachments && formData.attachments.length > 0) {
      for (const file of formData.attachments) {
        const filePath = `${userId}/${newContract.id}/${file.name}`;
        
        const { error: uploadError } = await supabase.storage
          .from("contract_attachments")
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) {
          toast.error(`Error uploading file: ${uploadError.message}`);
          continue;
        }

        const { data: publicUrlData } = supabase.storage
          .from("contract_attachments")
          .getPublicUrl(filePath);

        const { data: attachmentData, error: attachmentError } = await supabase
          .from("attachments")
          .insert([{
            name: file.name,
            type: file.type,
            size: file.size,
            url: filePath,
            contract_id: newContract.id,
            user_id: userId,
          }])
          .select()
          .single();

        if (attachmentError || !attachmentData) {
          toast.error(`Error saving attachment record: ${attachmentError?.message || "Unknown error"}`);
          continue;
        }

        attachments.push({
          id: attachmentData.id,
          name: attachmentData.name,
          type: attachmentData.type,
          size: attachmentData.size,
          url: publicUrlData.publicUrl,
          uploaded_at: attachmentData.uploaded_at,
          uploadedAt: new Date(attachmentData.uploaded_at),
        });
      }
    }

    const contract: Contract = {
      id: newContract.id,
      supplierName: newContract.supplier_name,
      organisationnumber: newContract.organisationnumber || undefined,
      importance: newContract.importance as 1 | 2 | 3 | 4 | 5,
      type: formData.type,
      expectedCost: newContract.expected_cost,
      paymentFrequency: formData.paymentFrequency,
      signedDate: new Date(newContract.signed_date),
      expiryDate: new Date(newContract.expiry_date),
      reminderPeriod: newContract.reminder_period as ReminderPeriod,
      reminderFrequency: newContract.reminder_frequency as ReminderFrequency,
      categoryId: newContract.category_id || undefined,
      category: undefined,
      contactPersons: [],
      attachments: attachments,
      created_at: newContract.created_at,
      updated_at: newContract.updated_at,
      currencyId: newContract.currency_id || undefined,
      currency: undefined,
      typeId: newContract.contract_type_id || undefined,
      userId: newContract.user_id,
      use_services_cost: newContract.use_services_cost || false,
      paymentType: (newContract.payment_type as PaymentType) || 'BankTransfer' as PaymentType,
      groupId: newContract.group_id || undefined,
      group: undefined,
      contractServices: []
    };

    await logContractChange(
      contract.id,
      'create',
      undefined,
      undefined,
      {
        supplierName: contract.supplierName,
        type: contract.type.name,
        expectedCost: contract.expectedCost
      }
    );

    return contract;
  };

  const updateContract = async ({
    id,
    formData,
    skipCostChangeTracking = false
  }: {
    id: string;
    formData: ContractFormData;
    skipCostChangeTracking?: boolean;
  }): Promise<Contract> => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }
    
    const paymentFrequencyString = JSON.stringify(formData.paymentFrequency);
    
    const normalizedSignedDate = new Date(formData.signedDate);
    normalizedSignedDate.setHours(12, 0, 0, 0);
    
    const normalizedExpiryDate = new Date(formData.expiryDate);
    normalizedExpiryDate.setHours(12, 0, 0, 0);

    try {
      // First, fetch the current contract data to compare changes
      const currentContract = await getContractById(id);
      
      // Prepare the update data
      const updateData = {
        supplier_name: formData.supplierName,
        organisationnumber: formData.organisationnumber,
        importance: formData.importance,
        type: formData.type.name,
        contract_type_id: formData.type.id,
        expected_cost: formData.expectedCost,
        currency_id: formData.currencyId,
        payment_frequency: paymentFrequencyString,
        signed_date: normalizedSignedDate.toISOString().split('T')[0],
        expiry_date: normalizedExpiryDate.toISOString().split('T')[0],
        reminder_period: formData.reminderPeriod,
        reminder_frequency: formData.reminderFrequency,
        category_id: formData.categoryId,
        use_services_cost: formData.use_services_cost || false,
        payment_type: formData.paymentType || 'BankTransfer',
      };

      // Update the contract
      const { data: updatedContract, error: contractError } = await supabase
        .from("contracts")
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (contractError || !updatedContract) {
        throw contractError || new Error("Failed to update contract");
      }
      
      // Handle contact persons separately
      const { error: deleteContactsError } = await supabase
        .from("contract_contacts")
        .delete()
        .eq('contract_id', id);

      if (deleteContactsError) {
        throw deleteContactsError;
      }

      // Insert new contact person relationships
      if (formData.contactPersonIds.length > 0) {
        const contactsToInsert = formData.contactPersonIds.map(contactId => ({
          contract_id: id,
          contact_id: contactId,
          user_id: userId,
        }));

        const { error: insertContactsError } = await supabase
          .from("contract_contacts")
          .insert(contactsToInsert);

        if (insertContactsError) {
          throw insertContactsError;
        }
      }

      // Log changes by comparing old and new values, but skip cost changes if flagged
      await logContractChanges(currentContract, formData, id, skipCostChangeTracking);

      return await getContractById(id);

    } catch (error) {
      console.error("Error in contract update transaction:", error);
      toast.error("Failed to update contract. Please try again.");
      throw error;
    }
  };

  const deleteContract = async (id: string): Promise<void> => {
    const currentContract = await getContractById(id);
    
    const { error } = await supabase
      .from("contracts")
      .delete()
      .eq('id', id);

    if (error) {
      toast.error(`Error deleting contract: ${error.message}`);
      throw error;
    }

    await logContractChange(
      id,
      'delete',
      undefined,
      {
        supplierName: currentContract.supplierName,
        type: currentContract.type.name
      },
      undefined
    );

    toast.success("Contract deleted successfully");
  };

  const getContractById = async (id: string): Promise<Contract> => {
    const { data, error } = await supabase
      .from("contracts")
      .select(`
        *,
        category:categories(*),
        currency:currencies(*),
        group:contract_groups(*)
      `)
      .eq('id', id)
      .single();

    if (error || !data) {
      toast.error(`Error fetching contract: ${error?.message || "Unknown error"}`);
      throw error || new Error("Failed to fetch contract");
    }

    let contractType: ContractType;
    
    if (data.contract_type_id) {
      const { data: contractTypeData, error: contractTypeError } = await supabase
        .from("contract_types")
        .select("*")
        .eq("id", data.contract_type_id)
        .single();
        
      if (contractTypeError || !contractTypeData) {
        contractType = {
          id: "",
          name: data.type,
          description: "",
          auto_renews: false,
          renewal_interval: undefined,
          renewal_unit: undefined,
          direction: undefined,
          created_at: undefined,
          updated_at: undefined,
        };
      } else {
        contractType = {
          id: contractTypeData.id,
          name: contractTypeData.name,
          description: contractTypeData.description || undefined,
          created_at: contractTypeData.created_at,
          updated_at: contractTypeData.updated_at,
          auto_renews: contractTypeData.auto_renews || false,
          renewal_interval: contractTypeData.renewal_interval,
          renewal_unit: contractTypeData.renewal_unit ? ensureValidPaymentFrequencyUnit(contractTypeData.renewal_unit) : undefined,
          direction: contractTypeData.direction as ContractDirection
        };
      }
    } else {
      const { data: contractTypeData, error: contractTypeError } = await supabase
        .from("contract_types")
        .select("*")
        .eq("name", data.type)
        .single();
        
      if (contractTypeError || !contractTypeData) {
        contractType = {
          id: "",
          name: data.type,
          description: "",
          auto_renews: false,
          renewal_interval: undefined,
          renewal_unit: undefined,
          direction: undefined,
          created_at: undefined,
          updated_at: undefined,
        };
      } else {
        contractType = {
          id: contractTypeData.id,
          name: contractTypeData.name,
          description: contractTypeData.description || undefined,
          created_at: contractTypeData.created_at,
          updated_at: contractTypeData.updated_at,
          auto_renews: contractTypeData.auto_renews || false,
          renewal_interval: contractTypeData.renewal_interval,
          renewal_unit: contractTypeData.renewal_unit ? ensureValidPaymentFrequencyUnit(contractTypeData.renewal_unit) : undefined,
          direction: contractTypeData.direction as ContractDirection
        };
      }
    }

    let paymentFrequency: PaymentFrequency;
    try {
      if (typeof data.payment_frequency === 'string') {
        try {
          const parsed = JSON.parse(data.payment_frequency);
          if (parsed && typeof parsed === 'object' && 'interval' in parsed && 'unit' in parsed) {
            paymentFrequency = {
              interval: parsed.interval,
              unit: parsed.unit
            };
          } else {
            paymentFrequency = parseLegacyPaymentFrequency(data.payment_frequency);
          }
        } catch (e) {
          paymentFrequency = parseLegacyPaymentFrequency(data.payment_frequency);
        }
      } else if (data.payment_frequency && typeof data.payment_frequency === 'object') {
        paymentFrequency = data.payment_frequency as PaymentFrequency;
      } else {
        paymentFrequency = {
          interval: 1,
          unit: "Month"
        };
      }
    } catch (e) {
      console.error("Error parsing payment frequency:", e);
      paymentFrequency = {
        interval: 1,
        unit: "Month"
      };
    }

    let category: Category | undefined = undefined;
    if (data.category) {
      category = {
        id: data.category.id,
        name: data.category.name,
        description: data.category.description || undefined,
        created_at: data.category.created_at,
        updated_at: data.category.updated_at,
      };
    }

    let currency = undefined;
    if (data.currency) {
      currency = {
        id: data.currency.id,
        code: data.currency.code,
        name: data.currency.name,
        symbol: data.currency.symbol,
        created_at: data.currency.created_at,
        updated_at: data.currency.updated_at,
        user_id: data.currency.user_id,
      };
    }

    let group = undefined;
    if (data.group) {
      group = {
        id: data.group.id,
        name: data.group.name,
        description: data.group.description || undefined,
        user_id: data.group.user_id,
        created_at: data.group.created_at,
        updated_at: data.updated_at,
        createdAt: new Date(data.group.created_at),
        updatedAt: new Date(data.group.updated_at)
      };
    }

    const contactPersons = await fetchContactPersons(id);
    const attachments = await fetchAttachments(id);
    
    let contractServices = [];
    try {
      const { data: contractServicesData, error: contractServicesError } = await supabase
        .from("contract_services")
        .select(`
          *,
          service:services(*)
        `)
        .eq("contract_id", id);
        
      if (!contractServicesError && contractServicesData && contractServicesData.length > 0) {
        contractServices = contractServicesData.map(service => ({
          id: service.id,
          contract_id: service.contract_id,
          service_id: service.service_id,
          service: service.service ? {
            id: service.service.id,
            name: service.service.name,
            description: service.service.description,
            service_type: service.service.service_type,
            cost: service.service.cost,
            currency_id: service.service.currency_id,
            currency: null, // Will be filled below if needed
            user_id: service.service.user_id,
            created_at: service.service.created_at,
            updated_at: service.service.updated_at
          } : undefined,
          quantity: service.quantity,
          user_id: service.user_id,
          created_at: service.created_at,
          updated_at: service.updated_at
        }));
        
        // Fetch currencies for services if needed
        for (const contractService of contractServices) {
          if (contractService.service && contractService.service.currency_id) {
            const { data: currencyData } = await supabase
              .from("currencies")
              .select("*")
              .eq("id", contractService.service.currency_id)
              .single();
              
            if (currencyData) {
              contractService.service.currency = {
                id: currencyData.id,
                code: currencyData.code,
                name: currencyData.name,
                symbol: currencyData.symbol,
                created_at: currencyData.created_at,
                updated_at: currencyData.updated_at,
                user_id: currencyData.user_id
              };
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching contract services:", error);
    }

    return {
      id: data.id,
      supplierName: data.supplier_name,
      organisationnumber: data.organisationnumber || undefined,
      importance: data.importance as 1 | 2 | 3 | 4 | 5,
      type: contractType,
      expectedCost: data.expected_cost,
      paymentFrequency: paymentFrequency,
      signedDate: new Date(data.signed_date),
      expiryDate: new Date(data.expiry_date),
      reminderPeriod: data.reminder_period as ReminderPeriod,
      reminderFrequency: data.reminder_frequency as ReminderFrequency,
      categoryId: data.category_id,
      category,
      contactPersons,
      attachments,
      created_at: data.created_at,
      updated_at: data.updated_at,
      currencyId: data.currency_id,
      currency,
      groupId: data.group_id,
      group,
      typeId: data.contract_type_id,
      userId: data.user_id,
      use_services_cost: data.use_services_cost || false,
      paymentType: (data.payment_type as PaymentType) || 'BankTransfer' as PaymentType,
      contractServices: contractServices
    };
  };

  const fetchAverageCostBySupplier = async (
    supplierName: string,
    organizationNumber: string | null,
    contractTypeId: string,
    enabled: boolean = true
  ): Promise<{ average: number; count: number }> => {
    try {
      console.log('Fetching average cost with params:', {
        supplierName,
        organizationNumber,
        contractTypeId
      });

      const { data, error } = await supabase
        .rpc('get_average_cost_by_supplier', {
          p_supplier_name: supplierName,
          p_organisation_number: organizationNumber,
          p_contract_type_id: contractTypeId
        });

      if (error) {
        console.error("Error fetching average cost:", error);
        return { average: 0, count: 0 };
      }

      console.log('Average cost data from function:', data);

      if (!data) {
        return { average: 0, count: 0 };
      }

      if (typeof data === 'object' && data !== null) {
        const result = data as { average?: number; count?: number };
        return { 
          average: result.average || 0, 
          count: result.count || 0 
        };
      }

      return { average: 0, count: 0 };
    } catch (error) {
      console.error("Error fetching average cost:", error);
      return { average: 0, count: 0 };
    }
  };

  const useContractsQuery = () => useQuery({
    queryKey: ["contracts"],
    queryFn: fetchContracts,
  });

  const useContractQuery = (id: string) => useQuery({
    queryKey: ["contracts", id],
    queryFn: () => getContractById(id),
    enabled: !!id,
  });

  const useUpdateContractMutation = () => useMutation({
    mutationFn: updateContract,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["contracts", data.id] });
    },
  });

  const useCreateContractMutation = () => useMutation({
    mutationFn: createContract,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contract created successfully");
    },
  });

  const useDeleteContractMutation = () => useMutation({
    mutationFn: deleteContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
    },
  });

  const useAverageCostBySupplierQuery = (
    supplierName: string,
    organizationNumber: string | null,
    contractTypeId: string,
    enabled: boolean = true
  ) => useQuery({
    queryKey: ["average-cost", supplierName, organizationNumber, contractTypeId],
    queryFn: () => fetchAverageCostBySupplier(supplierName, organizationNumber, contractTypeId),
    enabled: enabled && !!supplierName && !!contractTypeId,
  });

  const logContractChanges = async (
    currentContract: Contract,
    formData: ContractFormData,
    contractId: string,
    skipCostChangeTracking: boolean = false
  ) => {
    const fieldsToCheck = [
      { db: 'supplierName', form: 'supplierName', label: 'Supplier Name' },
      { db: 'organisationnumber', form: 'organisationnumber', label: 'Organization Number' },
      { db: 'importance', form: 'importance', label: 'Importance' },
      { db: 'use_services_cost', form: 'use_services_cost', label: 'Use Services Cost' },
      { db: 'paymentType', form: 'paymentType', label: 'Payment Type' },
    ];

    // Only check expectedCost if not skipping cost change tracking
    if (!skipCostChangeTracking) {
      fieldsToCheck.push({ db: 'expectedCost', form: 'expectedCost', label: 'Expected Cost' });
    }
    
    for (const field of fieldsToCheck) {
      const oldValue = currentContract[field.db as keyof Contract];
      const newValue = formData[field.form as keyof ContractFormData];
      
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        await logContractChange(
          contractId,
          'update',
          field.form,
          oldValue,
          newValue
        );
      }
    }
    
    const oldPaymentFrequency = currentContract.paymentFrequency;
    const newPaymentFrequency = formData.paymentFrequency;
    
    if (JSON.stringify(oldPaymentFrequency) !== JSON.stringify(newPaymentFrequency)) {
      await logContractChange(
        contractId,
        'update',
        'paymentFrequency',
        oldPaymentFrequency,
        newPaymentFrequency
      );
    }
    
    if (currentContract.signedDate.getTime() !== new Date(formData.signedDate).getTime()) {
      await logContractChange(
        contractId,
        'update',
        'signedDate',
        currentContract.signedDate,
        formData.signedDate
      );
    }
    
    if (currentContract.expiryDate.getTime() !== new Date(formData.expiryDate).getTime()) {
      await logContractChange(
        contractId,
        'update',
        'expiryDate',
        currentContract.expiryDate,
        formData.expiryDate
      );
    }
    
    if (
      currentContract.type.id !== formData.type.id || 
      currentContract.type.name !== formData.type.name
    ) {
      await logContractChange(
        contractId,
        'update',
        'type',
        { id: currentContract.type.id, name: currentContract.type.name },
        { id: formData.type.id, name: formData.type.name }
      );
    }
    
    if (currentContract.categoryId !== formData.categoryId) {
      await logContractChange(
        contractId,
        'update',
        'categoryId',
        currentContract.categoryId,
        formData.categoryId
      );
    }
    
    if (currentContract.currencyId !== formData.currencyId) {
      await logContractChange(
        contractId,
        'update',
        'currencyId',
        currentContract.currencyId,
        formData.currencyId
      );
    }
    
    if (currentContract.reminderPeriod !== formData.reminderPeriod) {
      await logContractChange(
        contractId,
        'update',
        'reminderPeriod',
        currentContract.reminderPeriod,
        formData.reminderPeriod
      );
    }
    
    if (currentContract.reminderFrequency !== formData.reminderFrequency) {
      await logContractChange(
        contractId,
        'update',
        'reminderFrequency',
        currentContract.reminderFrequency,
        formData.reminderFrequency
      );
    }
    
    const oldContactIds = new Set(currentContract.contactPersons.map(cp => cp.id));
    const newContactIds = new Set(formData.contactPersonIds);
    
    if (!setsEqual(oldContactIds, newContactIds)) {
      await logContractChange(
        contractId,
        'update',
        'contactPersons',
        Array.from(oldContactIds),
        Array.from(newContactIds)
      );
    }
  };

  const setsEqual = (a: Set<string>, b: Set<string>) => {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  };

  return {
    useContractsQuery,
    useContractQuery,
    useUpdateContractMutation,
    useCreateContractMutation,
    useDeleteContractMutation,
    useAverageCostBySupplierQuery,
    getContractById,
  };
}
